import { Checkmark } from '@ui/components/icons';
import { ROUTES } from '@ui/constants/routes';
import { NextLinkWithLocale } from '@ui/lib/i18n';
import { Checkbox } from '@ui/lib/park/ui/checkbox';
import { useUserTeamInfo } from '@ui/services/contract/hooks/GenesisAirdrop/useUserTeamInfo';
import { HStack, HstackProps, styled } from 'styled-system/jsx';
import { Address } from 'viem';

/** Resolve the user's active referral/team code (leader takes precedence). */
export const useShareReferralCode = (address?: Address) => {
  const { data: userTeamInfo, isLoading } = useUserTeamInfo(address);

  const referralCode =
    (userTeamInfo?.leaderTeam?.teamMemberCount ?? 0) > 0
      ? userTeamInfo?.leaderTeam?.code
      : (userTeamInfo?.memberTeam?.teamMemberCount ?? 0) > 0
      ? userTeamInfo?.memberTeam?.code
      : undefined;

  return { referralCode, isLoading };
};

const CreateCodeLink = () => (
  <NextLinkWithLocale
    href={ROUTES.AIRDROP}
    target="_blank"
    rel="noopener noreferrer"
  >
    <styled.span
      textStyle="text.small"
      color="#FFD600"
      cursor="pointer"
      whiteSpace="nowrap"
      _hover={{ textDecoration: 'underline' }}
    >
      Create a code?
    </styled.span>
  </NextLinkWithLocale>
);

/**
 * Lets the user toggle the invite code in the share image. When the user has no
 * code yet, the checkbox is hidden and only a "Create a code?" link is shown.
 */
export const ShareInviteCodeToggle = ({
  checked,
  onCheckedChange,
  hasCode,
  isLoading,
  ...rest
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  hasCode: boolean;
  isLoading: boolean;
} & HstackProps) => {
  // Avoid flashing "Create a code?" before team info resolves.
  if (isLoading) return null;

  return (
    <HStack w="full" {...rest}>
      {hasCode ? (
        <Checkbox
          indicatorOn={<Checkmark />}
          checked={checked}
          onCheckedChange={(d) => onCheckedChange(d.checked === true)}
          css={{
            gap: '6px',
            '& [data-part="control"]': {
              width: '18px',
              height: '18px',
              p: '1',
            },
            '& [data-part="label"]': {
              textStyle: 'text.small',
              color: 'greyWhite.lightGrey3',
            },
          }}
        >
          Show invite code
        </Checkbox>
      ) : (
        <CreateCodeLink />
      )}
    </HStack>
  );
};
